import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getCartFromStorage, 
  saveCartToStorage, 
  getWishlistFromStorage,
  saveWishlistToStorage 
} from '../utils/cartHelpers';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [darkMode, setDarkMode] = useState(false);
  const [orders, setOrders] = useState([]);
  const [userProfile, setUserProfile] = useState(null);

  // Initialize cart, wishlist, orders from localStorage
  useEffect(() => {
    setCart(getCartFromStorage());
    setWishlist(getWishlistFromStorage());
    
    // Load orders
    const savedOrders = localStorage.getItem('orders');
    if (savedOrders) {
      setOrders(JSON.parse(savedOrders));
    }

    // Load user profile
    const savedProfile = localStorage.getItem('userProfile');
    if (savedProfile) {
      setUserProfile(JSON.parse(savedProfile));
    }
    
    // Check for dark mode preference
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(savedDarkMode);
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  // Update cart in localStorage whenever it changes
  useEffect(() => {
    saveCartToStorage(cart);
  }, [cart]);

  // Update wishlist in localStorage whenever it changes
  useEffect(() => {
    saveWishlistToStorage(wishlist);
  }, [wishlist]);

  const addToCart = (product, quantity = 1) => {
    setCart(prevCart => {
      const existingItemIndex = prevCart.findIndex(item => item.id === product.id);
      
      if (existingItemIndex !== -1) {
        const newCart = [...prevCart];
        newCart[existingItemIndex].quantity += quantity;
        return newCart;
      } else {
        return [...prevCart, { ...product, quantity }];
      }
    });
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prevCart => {
      const newCart = [...prevCart];
      const itemIndex = newCart.findIndex(item => item.id === productId);
      if (itemIndex !== -1) {
        newCart[itemIndex].quantity = quantity;
      }
      return newCart;
    });
  };

  const clearCart = () => {
    setCart([]);
  };

  const toggleWishlist = (product) => {
    setWishlist(prevWishlist => {
      const existingIndex = prevWishlist.findIndex(item => item.id === product.id);
      if (existingIndex !== -1) {
        return prevWishlist.filter(item => item.id !== product.id);
      } else {
        return [...prevWishlist, product];
      }
    });
  };

  const isInWishlist = (productId) => {
    return wishlist.some(item => item.id === productId);
  };

  const toggleDarkMode = () => {
    setDarkMode(prev => {
      const newMode = !prev;
      localStorage.setItem('darkMode', newMode);
      if (newMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      return newMode;
    });
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const placeOrder = (orderData) => {
    const newOrder = {
      id: Date.now().toString(),
      orderNumber: `ORD-${Date.now()}`,
      items: [...cart],
      total: getCartTotal() * 1.1, // including tax
      status: 'Processing',
      trackingStages: [
        { stage: 'Order Placed', completed: true, date: new Date().toISOString() },
        { stage: 'Processing', completed: false, date: null },
        { stage: 'Shipped', completed: false, date: null },
        { stage: 'Out for Delivery', completed: false, date: null },
        { stage: 'Delivered', completed: false, date: null },
      ],
      customerInfo: orderData,
      createdAt: new Date().toISOString(),
      estimatedDelivery: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    };

    const updatedOrders = [newOrder, ...orders];
    setOrders(updatedOrders);
    localStorage.setItem('orders', JSON.stringify(updatedOrders));
    
    return newOrder;
  };

  const cancelOrder = (orderId) => {
    const updatedOrders = orders.map(order => 
      order.id === orderId 
        ? { ...order, status: 'Cancelled', cancelledAt: new Date().toISOString() }
        : order
    );
    setOrders(updatedOrders);
    localStorage.setItem('orders', JSON.stringify(updatedOrders));
  };

  const updateOrderTracking = (orderId, stageIndex) => {
    const updatedOrders = orders.map(order => {
      if (order.id === orderId) {
        const updatedStages = order.trackingStages.map((stage, idx) => {
          if (idx <= stageIndex) {
            return { ...stage, completed: true, date: stage.date || new Date().toISOString() };
          }
          return stage;
        });
        
        // Update order status based on stage
        let newStatus = order.status;
        if (stageIndex === 4) newStatus = 'Delivered';
        else if (stageIndex === 3) newStatus = 'Out for Delivery';
        else if (stageIndex === 2) newStatus = 'Shipped';
        else if (stageIndex === 1) newStatus = 'Processing';
        
        return { ...order, trackingStages: updatedStages, status: newStatus };
      }
      return order;
    });
    setOrders(updatedOrders);
    localStorage.setItem('orders', JSON.stringify(updatedOrders));
  };

  const updateUserProfile = (profileData) => {
    setUserProfile(profileData);
    localStorage.setItem('userProfile', JSON.stringify(profileData));
  };

  const value = {
    cart,
    wishlist,
    darkMode,
    orders,
    userProfile,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    toggleWishlist,
    isInWishlist,
    toggleDarkMode,
    getCartTotal,
    getCartItemCount,
    placeOrder,
    cancelOrder,
    updateOrderTracking,
    updateUserProfile,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};


